import React, {memo, useEffect, Suspense} from 'react';
import {useTranslation} from 'react-i18next';
import classNames from 'classnames';
import {CoreEvent, ViewMode} from '@taopix/taopix-core';
import {useTaopix} from '../Context/TaopixContext';
import AppAuth from '../Authentication/AppAuth';
import {LoadingOverlay, MatchesMediaQuery, Theme, ThemeName} from '@taopix/taopix-design-system';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import useSettings from '../../Hooks/useSettings';
import {useExperience} from '../Experience/ExperienceProvider';
import Header from '../AppHeader/AppHeader';
import Logo from '../AppHeader/Logo';
import {ROUTE_ASSISTANT} from '../../Constants';
import {useUserFlowState} from '../Context/UserFlow/UserFlowContext';
import {UserFlowType} from '../../Facade/UserFlow/UserFlowFacade';

export type AppProps = {
  children: any;
};

export type ControlCentreUser = {
  userName: string;
  userId: number;
  emailAddress: string;
  login: string;
  canCreateAccounts: boolean;
  isOrderSession: string;
  error: string;
};

// eslint-disable-next-line no-empty-pattern
const ForceLogin = ({children}: AppProps) => {
  // we need to load calendars because the layout palette requires it
  const {t} = useTranslation(['*', 'optionsandextras', 'calendars']);

  // get the app state
  const taopix = useTaopix();
  const settings = useSettings();
  const ui = useUiState();
  const userFlow = useUserFlowState();
  const {experience} = useExperience();

  // get some props from the core
  const server = taopix.core.get('server');

  const smallScreen = MatchesMediaQuery('(max-width: 799px)');

  useEffect(() => {
    if (smallScreen) {
      ui.dispatch(UserInterfaceActions.setIsSmallScreen(true));
    } else {
      ui.dispatch(UserInterfaceActions.setIsSmallScreen(false));
    }
  }, [smallScreen]);

  React.useLayoutEffect(() => {
    if (!taopix.state.loaded) return;
    if (settings.state.authentication.userRegistered) return;

    if (
      !experience.SETTINGS.guestAccess.allowGuestAccess &&
      !settings.state.authentication.userRegistered
    ) {
      userFlow.activateUserFlow(UserFlowType.ForceLogin);
    }
  }, [settings.state.authentication.userRegistered, taopix.state.loaded]);

  useEffect(() => {
    const tokens: string[] = [];
    if (!experience) return;

    tokens.push(
      taopix.core.subscribe(CoreEvent.CORE_BUSY, (msg: string, data: any) => {
        if (
          !experience.SETTINGS.guestAccess.allowGuestAccess &&
          !settings.state.authentication.userRegistered
        ) {
          ui.dispatch(UserInterfaceActions.setIsBusy(data));
        }
      })
    );

    return () => {
      tokens.forEach((token, index) => {
        taopix.core.unsubscribe(token);
        delete tokens[index];
      });
    };
  }, [experience, settings]);

  if (!taopix.state.loaded) {
    const loadingLabel = (() => {
      switch (true) {
        case typeof settings.state.project.stage === 'undefined': {
          return t('*:str_MessageStartingEditor');
        }
        case settings.state.project.stage === `#/${ROUTE_ASSISTANT}/`: {
          if (0 === window.location.hash.indexOf(`#/${ROUTE_ASSISTANT}/`)) {
            return t('*:str_MessageStartingAssistant');
          }
          break;
        }
      }

      return t('*:str_MessageStartingEditor');
    })();

    return (
      <LoadingOverlay
        id={'entry-loading-overlay'}
        key={'entry-loading-overlay'}
        open={true}
        label={loadingLabel}
        theme={ThemeName.Popouts}
        overlayColour={'transparent'}
      />
    );
  }

  const leftSideHeaderContent = (
    <>{!ui.state.isSmallScreen && experience.EDITOR.header.logo && <Logo />}</>
  );

  // The CSS classnames that define the view mode for use by the rest of the UI, not these are used in tailwind variants
  const viewModeClassNames = {
    [ViewMode.List]: 'editView listView',
    [ViewMode.Single]: 'editView pageView',
    [ViewMode.Grid]: 'gridView',
    [ViewMode.Preview]: 'previewView',
  };

  const appClasses = classNames(
    'flex',
    'flex-col',
    'flex-1',
    'max-w-full',
    'max-h-full',
    'min-h-0',

    // Add the view mode class name
    viewModeClassNames[ui.state.viewMode]
  );

  return (
    <>
      <Theme
        name={'tpx-theme-main'}
        allowBorder={false}
        allowCorners={false}
        allowTip={false}
        id="tpx-app"
        className={appClasses}
      >
        {/* AuthDialogs */}
        {!experience.SETTINGS.guestAccess.allowGuestAccess &&
        !settings.state.authentication.userRegistered ? (
          <>
            <Suspense
              fallback={
                <LoadingOverlay
                  open={true}
                  usePortal={true}
                  label={t('*:str_MessageStartingAssistant')}
                  theme={ThemeName.Popouts}
                />
              }
            >
              <LoadingOverlay
                id={'app-busy-overlay'}
                open={ui.state.busy || !taopix.state.loaded}
                label={t('*:str_MessagePleaseWait')}
                delay={500}
                key={'app-busy-overlay'}
                theme={ThemeName.Popouts}
              />
              <Header leftSideContent={leftSideHeaderContent} rightSideContent={[]}></Header>
              <AppAuth
                server={server}
                authentication={settings.state?.authentication}
                isForceLogin={true}
              />
            </Suspense>
          </>
        ) : (
          children
        )}
      </Theme>
    </>
  );
};

export default memo(ForceLogin);
